import { CardCvcElement, CardExpiryElement, CardNumberElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { useTranslation } from "react-i18next";
import { StatusCode } from "api/protocols";
import { GetCustomerFriends, GetCustomerPaymentMethods, ICustomerFriend } from "api/rpc/2024-04/customer/customer";
import { GetCustomerReservationSegment, PostReservationBooking } from "api/rpc/2024-04/customer/reservation";
import axios, { CancelToken } from "axios";
import Card from "components/card/Card";
import Spin from "components/spin/spin";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import moment from "moment";
import { ICreditCard } from "pages/guest/Customer/CustomerProfile";
import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { clientPortalUpdate } from "redux/actions/clientPortal";
import { dequeue, enqueue, showError, showSuccess } from "redux/actions/ui";
import { ISegment } from "redux/reducers/models/reservations";
import { ClientPortalNavigation } from "../../ClientPortalNavigation";
import { Select } from "components/select/index";
import "./clientPortalReservationBooking.scss";
import DataTable from "components/dataTable/DataTable";
import { LocaleCurrency } from "helpers/Locale";
import { bookingLineAmount } from "helpers/segmentHourlyPrice";
import { capitalize } from "helpers/Helpers";
import Checkbox from "components/form/checkbox/Checkbox";
import { ButtonNew as Button } from "components/buttonNew/index";
import useModal from "hooks/modals/useModal";
import Sheet from "components/sheet/Sheet";
import FormLayout from "components/form/FormLayout";
import CardSection from "components/cardSection/CardSection";
import {
  StripeCardCvcElementChangeEvent,
  StripeCardExpiryElementChangeEvent,
  StripeCardNumberElementChangeEvent,
} from "@stripe/stripe-js";
import Portal from "elements/Portal";
import Markdown from "components/markdown/Markdown";
import { PostPaymentMethod, PostSetup } from "api/rpc/2024-04/customer/paymentMethod";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { MOBILE_WIDTH } from "helpers/ScreenSizes";
import Panel from "components/panel/Panel";
import { ICustomer } from "redux/reducers/models/customer";
import GolferCard from "components/bookingPopUp/golferCard/GolferCard";
import ReservationGuestCountSelector from "../../../../../secure/facility/settings/reservations/reservationGuestCountSelector/ReservationGuestCountSelector";
import { getEffectiveReservationMaxBookingSize } from "../../../../../secure/facility/settings/reservations/reservationGuestCountSelector/reservationBookingSize";
import Callout from "components/callout/Callout";

/**
 * @property {string}
 * @description key is the name of the duration and the value is an array of segment ids.
 * @example
 * {
 *
 * '0.5 Hour': [10], '1 Hour': [10,11], '1.5 Hour': [10,11,12]
 *
 * }
 *  */
interface IDuration {
  [key: string]: Array<number>;
}

interface IState {
  customerCreditCards: Array<ICreditCard>;
  selectedCardId: number;
  segments: Array<ISegment>;
  durations: IDuration;
  /**Object containing arrays of the segment objects (used for displaying the time and price of the selected segments) */
  durationSegments: { [key: string]: Array<ISegment> };
  durationKey: string;
  differenceInMinutes: number;
  guests: number;
  acceptTerms: boolean;
  elementComplete: {
    cardNumber: boolean;
    cardExpiry: boolean;
    cardCvc: boolean;
  };
  saveCard: boolean;
  endTime: string;

  bookingParticipants: Array<Partial<ICustomer & { participant_id: number; customer_id: number }>>;
  guest1: string;
  guest2: string;
  guest3: string;
  guest4: string;
  guest5: string;
  guest6: string;
  guest7: string;
  guest8: string;
  guest9: string;
  guest10: string;
  guestSearchResult1: Array<ICustomer>;
  guestSearchResult2: Array<ICustomer>;
  guestSearchResult3: Array<ICustomer>;
  guestSearchResult4: Array<ICustomer>;
  guestSearchResult5: Array<ICustomer>;
  guestSearchResult6: Array<ICustomer>;
  guestSearchResult7: Array<ICustomer>;
  guestSearchResult8: Array<ICustomer>;
  guestSearchResult9: Array<ICustomer>;
  guestSearchResult10: Array<ICustomer>;

  friendsList: Array<ICustomerFriend>;
  [key: string]: any;
}

interface IParams {
  clientShortName: string;
  facilityShortName: string;
}

const MINUTES_PER_SEGMENT_DEFAULT = 30;

// const durationMap = [
//   { duration: 60, segmentMultFactor: 1 },
//   { duration: 30, segmentMultFactor: 2 },
// ];

export default function ClientPortalReservationBooking() {
  const { clientPortalStore } = useAppSelector(store => store);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const history = useHistory();
  const { clientShortName, facilityShortName } = useParams<IParams>();
  const { Option } = Select;
  const windowSize = useWindowSize();
  const maxBookingSize = useMemo(
    () => getEffectiveReservationMaxBookingSize(clientPortalStore?.reservation?.module?.max_booking_size),
    [clientPortalStore?.reservation?.module?.max_booking_size],
  );

  const [state, setState] = useState<IState>({
    customerCreditCards: [],
    selectedCardId: null,
    segments: [],
    durations: null,
    durationKey: "0.5 Hour",
    durationSegments: null,
    differenceInMinutes: MINUTES_PER_SEGMENT_DEFAULT,
    guests: 1,
    acceptTerms: false,
    elementComplete: {
      cardNumber: false,
      cardExpiry: false,
      cardCvc: false,
    },
    saveCard: false,
    endTime: "",

    bookingParticipants: [],
    guest1: "",
    guest2: "",
    guest3: "",
    guest4: "",
    guest5: "",
    guest6: "",
    guest7: "",
    guest8: "",
    guest9: "",
    guest10: "",
    guestSearchResult1: [],
    guestSearchResult2: [],
    guestSearchResult3: [],
    guestSearchResult4: [],
    guestSearchResult5: [],
    guestSearchResult6: [],
    guestSearchResult7: [],
    guestSearchResult8: [],
    guestSearchResult9: [],
    guestSearchResult10: [],

    friendsList: [],
  });

  const { state: newCardState, updateModal: updateNewCardModal, closeModal: closeNewCardModal } = useModal();
  const {
    state: paymentTermsState,
    updateModal: updatePaymentTermsModal,
    closeModal: closePaymentTermsModal,
  } = useModal();

  useEffect(() => {
    const source = axios.CancelToken.source();
    if (
      clientPortalStore?.selectedFacility?.client_id &&
      clientPortalStore?.reservation?.segment?.credit_card_required
    ) {
      void getCustomerPaymentMethods(clientPortalStore?.selectedFacility?.client_id, source.token);
    }

    setState(prev => ({
      ...prev,
      bookingParticipants: [
        {
          id: clientPortalStore?.user?.id,
          customer_id: clientPortalStore?.user?.id,
          full_name: clientPortalStore?.user?.full_name,
        },
      ],
    }));

    void getFriendsList();

    void checkActiveUser();
    if (clientPortalStore?.reservation) {
      void getSegments(source.token);
    }
    return () => {
      source.cancel();
    };
  }, []);

  useEffect(() => {
    if (state?.durations && state?.segments && state?.durationKey) {
      void setEndTime();
    }
  }, [state?.durations, state?.durationKey, state?.segments]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    const timeoutId = window.setTimeout(() => {
      void search(state.guest2, "guestSearchResult2", source.token);
    }, 500);
    return () => {
      source.cancel("Cancelled");
      window.clearTimeout(timeoutId);
      setState(prevState => ({ ...prevState, guestSearchResult2: [] }));
    };
  }, [state.guest2]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    const timeoutId = window.setTimeout(() => {
      void search(state.guest3, "guestSearchResult3", source.token);
    }, 500);
    return () => {
      source.cancel("Cancelled");
      window.clearTimeout(timeoutId);
      setState(prevState => ({ ...prevState, guestSearchResult3: [] }));
    };
  }, [state.guest3]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    const timeoutId = window.setTimeout(() => {
      void search(state.guest4, "guestSearchResult4", source.token);
    }, 500);
    return () => {
      source.cancel("Cancelled");
      window.clearTimeout(timeoutId);
      setState(prevState => ({ ...prevState, guestSearchResult4: [] }));
    };
  }, [state.guest4]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    const timeoutId = window.setTimeout(() => {
      void search(state.guest5, "guestSearchResult5", source.token);
    }, 500);
    return () => {
      source.cancel("Cancelled");
      window.clearTimeout(timeoutId);
      setState(prevState => ({ ...prevState, guestSearchResult5: [] }));
    };
  }, [state.guest5]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    const timeoutId = window.setTimeout(() => {
      void search(state.guest6, "guestSearchResult6", source.token);
    }, 500);
    return () => {
      source.cancel("Cancelled");
      window.clearTimeout(timeoutId);
      setState(prevState => ({ ...prevState, guestSearchResult6: [] }));
    };
  }, [state.guest6]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    const timeoutId = window.setTimeout(() => {
      void search(state.guest7, "guestSearchResult7", source.token);
    }, 500);
    return () => {
      source.cancel("Cancelled");
      window.clearTimeout(timeoutId);
      setState(prevState => ({ ...prevState, guestSearchResult7: [] }));
    };
  }, [state.guest7]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    const timeoutId = window.setTimeout(() => {
      void search(state.guest8, "guestSearchResult8", source.token);
    }, 500);
    return () => {
      source.cancel("Cancelled");
      window.clearTimeout(timeoutId);
      setState(prevState => ({ ...prevState, guestSearchResult8: [] }));
    };
  }, [state.guest8]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    const timeoutId = window.setTimeout(() => {
      void search(state.guest9, "guestSearchResult9", source.token);
    }, 500);
    return () => {
      source.cancel("Cancelled");
      window.clearTimeout(timeoutId);
      setState(prevState => ({ ...prevState, guestSearchResult9: [] }));
    };
  }, [state.guest9]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    const timeoutId = window.setTimeout(() => {
      void search(state.guest10, "guestSearchResult10", source.token);
    }, 500);
    return () => {
      source.cancel("Cancelled");
      window.clearTimeout(timeoutId);
      setState(prevState => ({ ...prevState, guestSearchResult10: [] }));
    };
  }, [state.guest10]);

  async function getFriendsList() {
    const friendsListRes = await GetCustomerFriends({ facility_id: clientPortalStore?.selectedFacility?.id }, true);
    if (friendsListRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error getting friends list")));
      return;
    }
    setState(prevState => ({ ...prevState, friendsList: friendsListRes.data }));
  }

  function checkActiveUser() {
    if (!clientPortalStore?.user) {
      void navigateToReservations();
    }
  }

  function navigateToReservations() {
    dispatch(clientPortalUpdate({ reservation: null }));
    history.push(`/portal/${clientShortName}/reservations/${facilityShortName ?? ""}`);
  }

  async function getSegments(cancelToken: CancelToken) {
    const segmentsRes = await GetCustomerReservationSegment(
      {
        module_id: clientPortalStore?.reservation?.module?.id,
        date: clientPortalStore?.reservation?.segment?.date,
        start_time: clientPortalStore?.reservation?.segment?.start_time,
        location_id: clientPortalStore?.reservation?.location?.id,
      },
      true,
    );
    if (segmentsRes?.status !== StatusCode.OK) {
      if (cancelToken && cancelToken.reason) {
        return;
      }
      dispatch(showError(t("Error getting segments")));
      return;
    }

    void setDurations(segmentsRes?.data as Array<ISegment>);
    setState(prevState => ({ ...prevState, segments: segmentsRes?.data }));
  }

  function setEndTime() {
    const selectedSegments = state?.durationSegments?.[state?.durationKey];

    if (!selectedSegments?.length) {
      return;
    }

    const totalDurationMinutes = selectedSegments.reduce((total, segment) => {
      return total + (segment?.blocks?.[0]?.duration_minutes ?? 0);
    }, 0);

    const endTime = moment(
      `${clientPortalStore?.reservation?.segment?.date} ${clientPortalStore?.reservation?.segment?.start_time}`,
    )
      .add(totalDurationMinutes, "minutes")
      .format("HH:mm:ss");

    setState(prevState => ({
      ...prevState,
      endTime,
    }));
  }

  function setDurations(segments: Array<ISegment>) {
    if (!segments?.length) {
      return;
    }

    const durations: IDuration = {};
    const durationSegments: { [key: string]: Array<ISegment> } = {};

    let currentSegments: Array<ISegment> = [];

    for (let i = 0; i < segments.length; i++) {
      const currentSegment = segments[i];

      // The first segment must be open and available.
      if (
        currentSegment?.blocked_type !== "open" ||
        currentSegment?.booking_id !== null ||
        currentSegment?.tournament_id !== null ||
        currentSegment?.league_id !== null
      ) {
        break;
      }

      if (i === 0) {
        currentSegments = [currentSegment];

        const durationMinutes = currentSegment?.blocks?.[0]?.duration_minutes;

        if (!durationMinutes) {
          break;
        }

        const hours = durationMinutes / 60;
        const timeKey = `${hours} ${hours > 1 ? "Hours" : "Hour"}`;

        durations[timeKey] = [currentSegment.id];
        durationSegments[timeKey] = [...currentSegments];

        continue;
      }

      const previousSegment = segments[i - 1];

      const previousBlockDuration = previousSegment?.blocks?.[0]?.duration_minutes;

      if (!previousBlockDuration) {
        break;
      }

      const expectedNextStart = moment(previousSegment.start_time, "HH:mm:ss")
        .add(previousBlockDuration, "minutes")
        .format("HH:mm:ss");

      // Ensure consecutive start_times based on the duration_minutes
      if (currentSegment.start_time !== expectedNextStart) {
        break;
      }

      currentSegments = [...currentSegments, currentSegment];

      const totalDurationMinutes = currentSegments.reduce((total, segment) => {
        return total + (segment?.blocks?.[0]?.duration_minutes ?? 0);
      }, 0);

      const hours = totalDurationMinutes / 60;
      const timeKey = `${hours} ${hours > 1 ? "Hours" : "Hour"}`;

      durations[timeKey] = currentSegments.map(segment => segment.id);
      durationSegments[timeKey] = [...currentSegments];

      if (hours === 5) {
        break;
      }
    }

    if (Object.keys(durations).length === 0) {
      dispatch(showError(t("Time unavailable, please select another time")));
      void navigateToReservations();
      return;
    }

    const durationKey = Object.keys(durations)[0];

    setState(prevState => ({
      ...prevState,
      durations,
      durationSegments,
      durationKey,
    }));
  }

  async function getCustomerPaymentMethods(clientId: number, cancelToken: CancelToken) {
    const customerPaymentMethodsRes = await GetCustomerPaymentMethods(
      { client_id: clientId, type: ["card"] },
      true,
      cancelToken,
    );
    if (customerPaymentMethodsRes.status !== StatusCode.OK) {
      if (cancelToken && cancelToken?.reason) {
        return;
      }
      dispatch(showError(t("Error getting customer payment methods")));
      return;
    }

    setState(prevState => ({
      ...prevState,
      customerCreditCards: customerPaymentMethodsRes.data?.card,
      selectedCardId:
        customerPaymentMethodsRes.data?.card?.length >= 1 ? customerPaymentMethodsRes.data?.card[0]?.id : undefined,
    }));
  }

  function handleDropDownChange(value: string | number, id: string) {
    setState(prevState => ({ ...prevState, [id]: value }));
  }

  function handleCardChange(cardId: number) {
    setState(prevState => ({ ...prevState, selectedCardId: cardId }));
  }

  function handleUpdateGuestsAmount(amount: number) {
    if (amount < state?.guests) {
      void handleRemoveGolfers(amount);
    }
    setState(prevState => ({ ...prevState, guests: amount }));
  }

  function handleRemoveGolfers(newQuantity: number) {
    const updatedBookingParticipants = [...state.bookingParticipants].filter((_, index) => index + 1 <= newQuantity);

    setState(prevState => ({
      ...prevState,
      bookingParticipants: updatedBookingParticipants,
      guest1: "",
      guest2: "",
      guest3: "",
      guest4: "",
      guest5: "",
      guest6: "",
    }));
  }

  function handleCheckboxChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { id, checked } = e.target;
    setState(prevState => ({ ...prevState, [id]: checked }));
  }

  function handleCardSectionChange(
    e: StripeCardNumberElementChangeEvent | StripeCardExpiryElementChangeEvent | StripeCardCvcElementChangeEvent,
  ) {
    setState(prevState => ({
      ...prevState,
      elementComplete: { ...prevState.elementComplete, [e.elementType]: e.complete },
    }));
  }

  async function handleAddNewCard() {
    if (!stripe || !elements) {
      dispatch(showError(t("Error occured before card could save. Please reattempt.")));
      return;
    }
    const tempCreditCards = [...state.customerCreditCards];
    dispatch(enqueue());
    try {
      const stripePaymentMethodRes = await stripe.createPaymentMethod({
        type: "card",
        card: elements.getElement("cardNumber"),
      });

      if (stripePaymentMethodRes.paymentMethod) {
        const setupRes = await PostSetup(null, false);

        if (setupRes.status !== StatusCode.OK) {
          dispatch(showError(t("Error adding payment method")));
        }
        if (stripePaymentMethodRes.paymentMethod.id !== "undefined") {
          const stripeConfirmCardRes = await stripe.confirmCardSetup(
            setupRes.data.setup_intent.client_secret as string,
            {
              payment_method: stripePaymentMethodRes.paymentMethod.id,
            },
          );
          if (stripeConfirmCardRes.error) {
            dispatch(showError(stripeConfirmCardRes.error.message));
          } else {
            const postPaymentRes = await PostPaymentMethod(
              {
                payment_method_id: stripeConfirmCardRes.setupIntent.payment_method,
                facility_id: clientPortalStore?.selectedFacility?.id,
                save_card: state.saveCard,
              },
              false,
            );
            if (postPaymentRes.status !== StatusCode.OK) {
              dispatch(showError(t("Error adding payment method")));
            } else {
              tempCreditCards.push(postPaymentRes.data as ICreditCard);
              setState(prevState => ({
                ...prevState,
                customerCreditCards: tempCreditCards,
                selectedCardId: postPaymentRes.data?.id,
              }));
              void closeAddNewCard();
              dispatch(showSuccess(t("Successfully added payment method")));
            }
          }
        }
      } else {
        dispatch(showError(stripePaymentMethodRes.error.message));
      }
    } catch (e) {
      console.error(e);
      dispatch(dequeue());
    }
    dispatch(dequeue());
  }

  async function handleBookReservation() {
    const currentDateTime = moment();
    const segmentDateTime = moment(
      `${clientPortalStore?.reservation?.segment?.date} ${clientPortalStore?.reservation?.segment?.start_time}`,
      "YYYY-MM-DD HH:mm:ss",
    );

    if (segmentDateTime.isBefore(currentDateTime)) {
      dispatch(showError(t("Start time is in the past, please try again")));
      void navigateToReservations();
      return;
    }

    if (
      !clientPortalStore?.reservation?.module?.id ||
      !clientPortalStore?.selectedFacility?.id ||
      !clientPortalStore?.reservation?.location?.id ||
      !state?.guests ||
      !state?.durations[state?.durationKey] ||
      !state?.acceptTerms
    ) {
      dispatch(showError(t("Booking error, please try again")));
      void navigateToReservations();
      return;
    }

    // Set reservation booking participants
    const participants = [...state.bookingParticipants]?.map(participant => ({
      customer_id: participant?.id,
      guest: false,
    }));
    if (state?.guests > state?.bookingParticipants?.length) {
      const diff = state?.guests - state?.bookingParticipants?.length;
      for (let i = 0; i < diff; i++) {
        participants.push({
          customer_id: state.bookingParticipants[0]?.id,
          guest: true,
        });
      }
    }

    const reservationRes = await PostReservationBooking(
      {
        module_id: clientPortalStore?.reservation?.module?.id,
        facility_id: clientPortalStore?.selectedFacility?.id,
        location_id: clientPortalStore?.reservation?.location?.id,
        date: clientPortalStore?.reservation?.segment?.date,
        quantity: state?.guests,
        segment_ids: state.durations[state.durationKey],
        accept_payment_terms: state.acceptTerms,
        customer_payment_method_id: state.selectedCardId ? state?.selectedCardId : undefined,
        reservation_participants: participants,
      },
      true,
    );
    if (reservationRes?.status !== StatusCode.OK) {
      dispatch(
        showError(typeof reservationRes?.data === "string" ? reservationRes?.data : t("Error booking reservation")),
      );
      void navigateToReservations();
      return;
    }

    dispatch(
      clientPortalUpdate({
        reservation: {
          ...clientPortalStore?.reservation,
          booking: reservationRes?.data,
          bookedSegments: state?.durationSegments[state?.durationKey],
          endTime: moment(state?.endTime, "hh:mm:ss").format("h:mm A"),
        },
      }),
    );

    //Booking successful, go to confirmation page
    history.push(
      `/portal/${clientShortName}/reservations/${
        facilityShortName ?? clientPortalStore?.selectedFacility?.short_name
      }/confirmation?token=${reservationRes?.data?.token as string}&handle=${
        clientPortalStore?.reservation?.module?.handle
      }&location_id=${clientPortalStore?.reservation?.location?.id}`,
    );
  }

  function displayTerms(e: React.MouseEvent) {
    e.stopPropagation();
    e.preventDefault();
    updatePaymentTermsModal({ isOpen: true });
  }

  const disableConfirmButton =
    !state?.guests ||
    !clientPortalStore?.selectedFacility?.id ||
    !clientPortalStore?.reservation ||
    !state.durations ||
    !state?.acceptTerms ||
    (clientPortalStore?.reservation?.segment?.credit_card_required && state?.selectedCardId == null)
      ? true
      : false;

  // Check if card inputs are filled
  const elementsComplete =
    state.elementComplete.cardNumber === true &&
    state.elementComplete.cardExpiry === true &&
    state.elementComplete.cardCvc === true;

  function closeAddNewCard() {
    elements.getElement(CardNumberElement).clear();
    elements.getElement(CardExpiryElement).clear();
    elements.getElement(CardCvcElement).clear();
    setState(prevState => ({ ...prevState, newCreditCardName: "" }));
    void closeNewCardModal();
  }

  function handleRemoveGuest(index: number) {
    const updatedBookingParticipants = [...state.bookingParticipants];
    updatedBookingParticipants.splice(index, 1);

    setState(prevState => ({
      ...prevState,
      bookingParticipants: updatedBookingParticipants,
      guest1: "",
      guest2: "",
      guest3: "",
      guest4: "",
      guest5: "",
      guest6: "",
      guestSearchResult1: [],
      guestSearchResult2: [],
      guestSearchResult3: [],
      guestSearchResult4: [],
      guestSearchResult5: [],
      guestSearchResult6: [],
    }));
  }

  const handleCustomerSearch = (query: string, name: string) => {
    setState(prevState => ({ ...prevState, [name]: query }));
  };

  function handleCustomerSelection(customer: ICustomer) {
    const updatedBookingParticipants = [...state.bookingParticipants];
    updatedBookingParticipants.push({ ...customer, customer_id: customer?.id });
    setState(prevState => ({
      ...prevState,
      guest1: "",
      guest2: "",
      guest3: "",
      guest4: "",
      guest5: "",
      guest6: "",
      guestSearchResult1: [],
      guestSearchResult2: [],
      guestSearchResult3: [],
      guestSearchResult4: [],
      guestSearchResult5: [],
      guestSearchResult6: [],
      bookingParticipants: updatedBookingParticipants,
    }));
  }

  function renderFriendsList(playerSearchResult: string) {
    //Display the search results if the array contains values
    if (state[playerSearchResult]?.length > 0) {
      const guestsList: Array<ICustomerFriend> = state[playerSearchResult];
      return guestsList.filter(
        guests => !state.bookingParticipants.some(participants => participants.customer_id === guests.id),
      );
    } else {
      //Else return the entire friends list
      return state.friendsList.filter(
        guests => !state.bookingParticipants.some(participants => participants.customer_id === guests.id),
      );
    }
  }

  const search = async (playerSearchQuery: string, playerSearchResult: string, cancelToken: CancelToken) => {
    try {
      if (playerSearchQuery === "") {
        setState(prevState => ({ ...prevState, [playerSearchResult]: [] }));
        return;
      } else {
        setState(prevState => ({ ...prevState, searching: true }));
        // let friendsResults: any;
        // if (playerSearchResult === "customerSearchResult") {
        //   friendsResults = await searchCustomers(cancelToken);
        // } else {
        const friendsResults = await searchFriends(playerSearchQuery, cancelToken);
        // }
        setState(prevState => ({ ...prevState, searching: false, [playerSearchResult]: friendsResults }));
      }
    } catch (error) {
      console.log("err", error);
    }
    return;
  };

  async function searchFriends(playerSearchQuery: string, cancelToken: CancelToken) {
    const searchFriendsRes = await GetCustomerFriends(
      { search: playerSearchQuery, facility_id: clientPortalStore?.selectedFacility?.id },
      false,
      cancelToken,
    );
    if (searchFriendsRes.status !== StatusCode.OK) {
      if (searchFriendsRes.message !== "Cancelled") {
        dispatch(showError(t("Error getting friends list")));
      }
      return [];
    }
    return searchFriendsRes.data;
  }

  return (
    <>
      <div className="client-portal-reservation-booking">
        <ClientPortalNavigation showNavOptions />
        <div className="client-portal-reservation-booking-facility-container">
          <Card>
            <Card.Section>
              <div className="client-portal-reservation-booking-flex-row">
                <span className="facility-logo">
                  {clientPortalStore?.selectedFacility?.logo_source && (
                    <img
                      className="facility-logo"
                      src={clientPortalStore?.selectedFacility?.logo_source}
                      alt={t("Facility Logo")}
                    />
                  )}
                </span>
                {clientPortalStore?.selectedFacility && (
                  <div className="client-portal-reservation-booking-facility-container-group">
                    <p className="client-portal-reservation-booking-primary-text">
                      {clientPortalStore?.selectedFacility?.long_name}
                    </p>
                    <p className="client-portal-reservation-booking-secondary-text">
                      {clientPortalStore?.selectedFacility?.address_line_1}, {clientPortalStore?.selectedFacility?.city}
                      , {clientPortalStore?.selectedFacility?.province_name},{" "}
                      {clientPortalStore?.selectedFacility?.postal}
                    </p>
                  </div>
                )}
              </div>
            </Card.Section>
          </Card>
        </div>
        <div className="client-portal-reservation-booking-main-container">
          <Card>
            <Card.Section>
              <p className="client-portal-reservation-booking-primary-text">
                {clientPortalStore?.reservation?.location?.title} -{" "}
                {moment(clientPortalStore?.reservation?.segment?.start_time, "hh:mm:ss").format("h:mm A")} -{" "}
                {state?.endTime ? moment(state?.endTime, "hh:mm:ss").format("h:mm A") : null}
              </p>
              <p className="client-portal-reservation-booking-tertiary-text">
                {moment(clientPortalStore?.reservation?.segment?.date).format("dddd")}{" "}
                {moment(clientPortalStore?.reservation?.segment?.date).format("LL")}
              </p>

              <div className="client-portal-reservation-booking-flex-row">
                <div className="client-portal-reservation-booking-button-group">
                  <p className="client-portal-reservation-booking-tertiary-text">{t("Duration")}</p>
                  {state?.durations ? (
                    <Select
                      defaultValue={state?.durationKey}
                      onChange={(value: string) => handleDropDownChange(value, "durationKey")}
                    >
                      {Object?.keys(state?.durations)?.map((durationKey, durationIndex) => {
                        return (
                          <Option key={durationIndex} value={durationKey} name={durationKey}>
                            {durationKey}
                          </Option>
                        );
                      })}
                    </Select>
                  ) : (
                    <div style={{ height: "40px" }}>
                      <Spin />
                    </div>
                  )}
                </div>

                <div className="client-portal-reservation-booking-button-group">
                  <p className="client-portal-reservation-booking-tertiary-text">{t("Guests")}</p>
                  <ReservationGuestCountSelector
                    name="guests"
                    value={state.guests}
                    maxBookingSize={maxBookingSize}
                    onChange={handleUpdateGuestsAmount}
                  />
                </div>
              </div>

              <div className="reservation-booking-participants-container mt-4 mb-4">
                {[...Array(state?.guests).keys()].map(index => {
                  if (state?.bookingParticipants[index]) {
                    return (
                      <GolferCard
                        closable={index > 0}
                        removeGolfer={() => handleRemoveGuest(index)}
                        name={state?.bookingParticipants[index]?.full_name}
                        bookingEngineCard={true}
                        key={index}
                      />
                    );
                  } else {
                    const searchResultKey = "guestSearchResult".concat((index + 1).toString());
                    const queryKey = "guest".concat((index + 1).toString());
                    return (
                      <Select
                        key={index}
                        showSearch
                        onSearch={(query: string) => handleCustomerSearch(query, queryKey)}
                        onChange={(_, customer: ICustomer) => handleCustomerSelection(customer)}
                        allowClear
                        searchValue={state[queryKey]}
                        showDropDownOnFocus={true}
                        searching={state.searching}
                        disabled={index + 1 <= state.guests ? false : true}
                        placeholder={t("Guest")}
                        usePortal
                      >
                        {renderFriendsList(searchResultKey)?.map((friend: ICustomerFriend, index: number) => {
                          return (
                            <Option key={index} value={friend.id} extraValues={friend}>
                              <span>{friend.full_name}</span>
                            </Option>
                          );
                        })}
                      </Select>
                    );
                  }
                })}
              </div>

              {state?.durationSegments && (
                <div className="client-portal-reservation-booking-button-group">
                  {console.log("duration seg", state?.durationSegments)}
                  <DataTable
                    columns={[
                      { label: t("Time"), width: "50%" },
                      { label: t("Price"), width: "50%" },
                    ]}
                  >
                    {state.durationSegments[state.durationKey]?.map((segment, segmentIndex) => {
                      console.log("segment index", segmentIndex, "segment", segment);

                      return (
                        <tr key={segmentIndex}>
                          <td>{moment(segment?.start_time, "hh:mm:ss").format("h:mm A")}</td>
                          <td>
                            {bookingLineAmount(state.durationSegments[state.durationKey], segmentIndex, false) != null && (
                              <LocaleCurrency
                                amount={
                                  bookingLineAmount(state.durationSegments[state.durationKey], segmentIndex, false) ??
                                  undefined
                                }
                              />
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </DataTable>
                </div>
              )}
            </Card.Section>
          </Card>

          <Card>
            <Card.Section>
              <p className="client-portal-reservation-booking-tertiary-text">{t("Booking Info")}</p>

              {clientPortalStore?.reservation?.module?.require_booking_approval && (
                <div className="mb-2">
                  <Callout
                    type="warning"
                    title={t("Facility approval required")}
                    content={t(
                      "Submitting this reservation will send a request to the facility. It will stay pending until it is accepted.",
                    )}
                  />
                </div>
              )}

              {clientPortalStore?.reservation?.segment?.credit_card_required ? (
                <div className="reservation-booking-dropdown">
                  <Select
                    label={t(
                      "Credit card is required to hold your booking, it will only be charged in accordance to our Payment Terms and Conditions",
                    )}
                    defaultValue={state.selectedCardId}
                    onChange={(value: number) => handleCardChange(value)}
                  >
                    <li className="ui-select-dropdown-list-item" onClick={() => updateNewCardModal({ isOpen: true })}>
                      <p className="text-md">{t("Add New Card")}</p>
                    </li>
                    {state.customerCreditCards?.map((card, index) => {
                      return (
                        <Option key={index} value={card?.id} name={card?.brand}>
                          {capitalize(card?.brand)}
                          {" •••• ".concat(card?.last4)}
                        </Option>
                      );
                    })}
                  </Select>
                </div>
              ) : null}

              <div className="client-portal-reservation-booking-payment-terms-container">
                <Checkbox
                  id="acceptTerms"
                  size="medium"
                  value={state.acceptTerms}
                  checked={state.acceptTerms}
                  onChange={handleCheckboxChange}
                  label={
                    <span>
                      {t("I agree to the")}{" "}
                      <span onClick={e => displayTerms(e)} className="client-portal-reservation-booking-conditions">
                        {t("Booking and Payment terms of")}{" "}
                        {clientPortalStore?.selectedFacility?.long_name ?? facilityShortName}
                      </span>
                    </span>
                  }
                />
              </div>

              <div className="client-portal-reservation-booking-confirm-button-group">
                <Button size="medium" type="secondary" onClick={navigateToReservations} block>
                  {t("Back")}
                </Button>
                <Button
                  size="medium"
                  type="primary"
                  disabled={disableConfirmButton}
                  onClick={handleBookReservation}
                  block
                >
                  {clientPortalStore?.reservation?.module?.require_booking_approval
                    ? t("Request Booking")
                    : t("Book")}
                </Button>
              </div>
            </Card.Section>
          </Card>
        </div>
      </div>

      {/* Add new credit card modal */}
      <Portal isMounted={newCardState?.isOpen && windowSize.width > MOBILE_WIDTH}>
        <Sheet
          size="small"
          open={newCardState?.isOpen}
          closable
          title={t("Add New Credit Card")}
          cancelText={t("Cancel")}
          okText={t("Add Card")}
          onOk={handleAddNewCard}
          okDisabled={!elementsComplete ? true : false}
          onCancel={closeAddNewCard}
          backDropCancel={false}
        >
          <div>
            <FormLayout>
              <FormLayout.Group>
                <CardSection onChange={handleCardSectionChange} type="one-row" />
              </FormLayout.Group>
              <FormLayout.Group>
                <Checkbox
                  id="saveCard"
                  size="small"
                  onChange={handleCheckboxChange}
                  label={t("Save card for future bookings")}
                />
              </FormLayout.Group>
            </FormLayout>
          </div>
        </Sheet>
      </Portal>

      <Panel
        open={newCardState?.isOpen && windowSize.width < MOBILE_WIDTH}
        onClose={closeAddNewCard}
        title={t("Add New Credit Card")}
        animate
        primaryAction={{
          onClick: handleAddNewCard,
          label: t("Add Card"),
          disabled: !elementsComplete,
        }}
        secondaryAction={{
          onClick: closeAddNewCard,
          label: t("Cancel"),
        }}
      >
        <div>
          <FormLayout>
            <FormLayout.Group>
              {windowSize.width <= MOBILE_WIDTH && <CardSection onChange={handleCardSectionChange} type="one-row" />}
            </FormLayout.Group>
            <FormLayout.Group>
              <Checkbox
                id="saveCard"
                size="small"
                onChange={handleCheckboxChange}
                label={t("Save card for future bookings")}
              />
            </FormLayout.Group>
          </FormLayout>
        </div>
      </Panel>

      {/* Payment Terms and Conditions Modal */}
      <Portal isMounted={paymentTermsState?.isOpen && windowSize.width > MOBILE_WIDTH}>
        <Sheet
          size="small"
          open={paymentTermsState?.isOpen}
          closable
          title={`${clientPortalStore?.selectedFacility?.long_name} Terms and Conditions`}
          cancelText={t("Close")}
          okText={t("Accept")}
          onOk={() => {
            setState(prevState => ({ ...prevState, acceptTerms: true }));
            void closePaymentTermsModal();
          }}
          onCancel={() => closePaymentTermsModal()}
          backDropCancel={false}
        >
          {clientPortalStore?.reservation?.module?.payment_terms && (
            <>
              <p className="client-portal-reservation-booking-tertiary-text">{t("Payment Terms")}</p>
              <Markdown markdownText={clientPortalStore?.reservation?.module?.payment_terms ?? ""} />
            </>
          )}

          <br />
          {clientPortalStore?.reservation?.module?.booking_terms && (
            <>
              <p className="client-portal-reservation-booking-tertiary-text">{t("Booking Terms")}</p>
              <Markdown markdownText={clientPortalStore?.reservation?.module?.booking_terms ?? ""} />
            </>
          )}
        </Sheet>
      </Portal>

      <Panel
        open={paymentTermsState?.isOpen && windowSize.width <= MOBILE_WIDTH}
        onClose={() => closePaymentTermsModal()}
        title={`${clientPortalStore?.selectedFacility?.long_name} Terms and Conditions`}
        animate
        primaryAction={{
          onClick: () => {
            setState(prevState => ({ ...prevState, acceptTerms: true }));
            void closePaymentTermsModal();
          },
          label: t("Accept"),
        }}
        secondaryAction={{
          onClick: () => closePaymentTermsModal(),
          label: t("Close"),
        }}
      >
        {clientPortalStore?.reservation?.module?.payment_terms && (
          <>
            <p className="client-portal-reservation-booking-tertiary-text">{t("Payment Terms")}</p>
            <Markdown markdownText={clientPortalStore?.reservation?.module?.payment_terms ?? ""} />
          </>
        )}

        <br />
        {clientPortalStore?.reservation?.module?.booking_terms && (
          <>
            <p className="client-portal-reservation-booking-tertiary-text">{t("Booking Terms")}</p>
            <Markdown markdownText={clientPortalStore?.reservation?.module?.booking_terms ?? ""} />
          </>
        )}
      </Panel>
    </>
  );
}
